// Issue detail page
// Reference: spec.md US3 - view issue details

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui';
import { api, getAttachmentUrl } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import type { IssueWithRelations, IssueStatus, IssueCategory, User, Attachment } from '@propman/shared';

// Status badge colors
const STATUS_COLORS: Record<IssueStatus, { bg: string; text: string; border: string }> = {
  reported: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  job_awarded_awaiting_appointment: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  completed: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
};

// Status labels
const STATUS_LABELS: Record<IssueStatus, string> = {
  reported: 'Reported',
  in_progress: 'In Progress',
  job_awarded_awaiting_appointment: 'Awaiting Appointment',
  completed: 'Completed',
};

// Category labels
const CATEGORY_LABELS: Record<IssueCategory, string> = {
  mechanical: 'Mechanical',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  handyman: 'Handyman',
  cleaning: 'Cleaning',
  security_access: 'Security & Access',
  fire_safety: 'Fire Safety',
  lift_elevator: 'Lift / Elevator',
  pest_control: 'Pest Control',
  landscaping_grounds: 'Landscaping',
  appliances: 'Appliances',
  it_intercom: 'IT & Intercom',
  other: 'Other',
};

// Status flow for managers
const STATUS_FLOW: IssueStatus[] = [
  'reported',
  'in_progress',
  'job_awarded_awaiting_appointment',
  'completed',
];

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function IssueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [issue, setIssue] = useState<IssueWithRelations | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [lightboxAttachment, setLightboxAttachment] = useState<Attachment | null>(null);

  const isManager = user?.role === 'manager' || user?.role === 'owner';

  async function loadIssue(showLoadingState = true) {
    if (!id) return;

    if (showLoadingState) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await api.get<{ issue: IssueWithRelations }>(`/issues/${id}`);
      setIssue(response.issue);
    } catch (err) {
      console.error('Failed to load issue:', err);
      setError('Failed to load issue. It may not exist or you may not have access.');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadIssue();
  }, [id]);

  async function loadUsers() {
    try {
      const response = await api.get<{ users: User[] }>('/users');
      setUsers(response.users.filter(u => u.role === 'manager' || u.role === 'owner'));
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }

  async function handleStatusChange(newStatus: IssueStatus) {
    if (!issue || isUpdatingStatus) return;

    setIsUpdatingStatus(true);
    setShowStatusMenu(false);

    try {
      await api.patch(`/issues/${issue.id}/status`, { status: newStatus });
      // Reload the full issue to get updated relations
      await loadIssue(false);
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update issue status');
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  async function handleAssign() {
    if (!issue || !selectedAssignee || isAssigning) return;

    setIsAssigning(true);

    try {
      await api.patch(`/issues/${issue.id}/assign`, { assignedTo: selectedAssignee });
      // Reload the full issue to get updated relations
      await loadIssue(false);
      setShowAssignModal(false);
      setSelectedAssignee('');
    } catch (err) {
      console.error('Failed to assign issue:', err);
      alert('Failed to assign issue');
    } finally {
      setIsAssigning(false);
    }
  }

  function openAssignModal() {
    loadUsers();
    setSelectedAssignee(issue?.assignedTo || '');
    setShowAssignModal(true);
  }

  if (isLoading) {
    return (
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading issue...</p>
        </div>
      </div>
    );
  }

  if (error || !issue) {
    return (
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Issue not found</h3>
          <p className="mt-2 text-gray-600">{error || 'The issue you are looking for does not exist.'}</p>
          <Link to="/issues">
            <Button className="mt-4" variant="secondary">Back to Issues</Button>
          </Link>
        </div>
      </div>
    );
  }

  const statusColor = STATUS_COLORS[issue.status];

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      {/* Back button */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6">
          {/* Reference and status */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-mono text-gray-500">{issue.referenceNumber}</span>
            <div className="relative">
              {isManager && issue.status !== 'completed' ? (
                <>
                  <button
                    onClick={() => setShowStatusMenu(!showStatusMenu)}
                    disabled={isUpdatingStatus}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${statusColor.bg} ${statusColor.text} ${statusColor.border} hover:opacity-80 transition-opacity`}
                  >
                    {isUpdatingStatus ? (
                      <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2" />
                    ) : null}
                    {STATUS_LABELS[issue.status]}
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showStatusMenu && (
                    <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1">
                        {STATUS_FLOW.map((status) => (
                          <button
                            key={status}
                            onClick={() => handleStatusChange(status)}
                            className={`w-full text-left px-4 py-2 text-sm ${
                              status === issue.status
                                ? 'bg-gray-100 text-gray-900 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {STATUS_LABELS[status]}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <span
                  className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${statusColor.bg} ${statusColor.text} ${statusColor.border}`}
                >
                  {STATUS_LABELS[issue.status]}
                </span>
              )}
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900">{issue.title}</h1>

          {/* Location */}
          <p className="mt-2 text-gray-600">
            <svg className="w-4 h-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {issue.unit.property.name} - {issue.unit.identifier}
            {issue.unit.floor && ` (Floor ${issue.unit.floor})`}
          </p>

          {/* Category badge */}
          <div className="mt-4">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {CATEGORY_LABELS[issue.category]}
            </span>
          </div>
        </div>

        {/* Description */}
        <div className="border-t border-gray-200 px-6 py-4">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Description</h2>
          <p className="text-gray-700 whitespace-pre-wrap">{issue.description}</p>
        </div>

        {/* Attachments */}
        {issue.attachments && issue.attachments.length > 0 && (
          <div className="border-t border-gray-200 px-6 py-4">
            <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
              Attachments ({issue.attachments.length})
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {issue.attachments.map((attachment) => {
                const attachmentUrl = getAttachmentUrl(issue.id, attachment.id);
                const isImage = attachment.fileType.startsWith('image/');

                return (
                  <button
                    key={attachment.id}
                    type="button"
                    onClick={() => isImage ? setLightboxAttachment(attachment) : window.open(attachmentUrl, '_blank')}
                    className="text-left border border-gray-200 rounded-lg overflow-hidden hover:border-primary-300 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {isImage ? (
                      <div className="aspect-video bg-gray-100 relative">
                        <img
                          src={attachmentUrl}
                          alt={attachment.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-colors flex items-center justify-center">
                          <svg className="w-8 h-8 text-white opacity-0 hover:opacity-100 drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-video bg-gray-50 flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="p-2 bg-white">
                      <p className="text-sm font-medium text-gray-900 truncate">{attachment.filename}</p>
                      <p className="text-xs text-gray-500">
                        {(attachment.fileSize / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Meta info */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reported by</p>
              <p className="mt-1 text-sm text-gray-900">{issue.reporter.name}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created</p>
              <p className="mt-1 text-sm text-gray-900">{formatDate(issue.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last updated</p>
              <p className="mt-1 text-sm text-gray-900">{formatDate(issue.updatedAt)}</p>
            </div>
          </div>
        </div>

        {/* Manager actions */}
        {isManager && (
          <div className="border-t border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Assigned to</p>
                <p className="mt-1 text-sm text-gray-900">
                  {issue.assignedTo ? (
                    users.find(u => u.id === issue.assignedTo)?.name || 'Assigned'
                  ) : (
                    <span className="text-gray-400">Unassigned</span>
                  )}
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={openAssignModal}>
                {issue.assignedTo ? 'Reassign' : 'Assign'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
              onClick={() => setShowAssignModal(false)}
            />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Assign Issue</h2>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-500"
                  onClick={() => setShowAssignModal(false)}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="assignee" className="block text-sm font-medium text-gray-700">
                    Select assignee
                  </label>
                  <select
                    id="assignee"
                    value={selectedAssignee}
                    onChange={(e) => setSelectedAssignee(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">Select a user...</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAssign}
                    isLoading={isAssigning}
                    disabled={!selectedAssignee}
                  >
                    Assign
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {lightboxAttachment && issue && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={() => setLightboxAttachment(null)}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => setLightboxAttachment(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            aria-label="Close"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Download button */}
          <a
            href={getAttachmentUrl(issue.id, lightboxAttachment.id)}
            download={lightboxAttachment.filename}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-4 left-4 text-white hover:text-gray-300 z-10 flex items-center gap-2"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </a>

          {/* Image */}
          <img
            src={getAttachmentUrl(issue.id, lightboxAttachment.id)}
            alt={lightboxAttachment.filename}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Filename */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-4 py-2 rounded">
            {lightboxAttachment.filename}
          </div>
        </div>
      )}
    </div>
  );
}

export default IssueDetailPage;
